// src/components/SectionThree/SectionThree.js
import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import ReactECharts from "echarts-for-react";
import { viewState, tiffFilesOne, zTranslationsOne } from "../../config/rasterConfig";
import GenericDeckGLMap from "../DeckGLMaps/GenericDeckGLMap";
import { MaskedStackedArea, MyChord } from "../charts/charts";
import "./SectionThree.css";
import { ImportExport } from "@mui/icons-material";
import { Typography } from "@mui/material";
import CollapsibleCitation from "../tools/CollapsibleCitation";
import InformationPopUp from "../tools/InformationPopUp";

export default function UsoSuelo() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { amount: 0.5, once: false });


  return (
    <section
      ref={sectionRef}
      className="h-screen snap-start bg-gradient-to-b from-[#324130] to-[#92843B] text-white"
    >
      <div className="w-full grid grid-cols-1 lg:grid-cols-2">
        {/* ─── LEFT COLUMN: AREA + CHORD ─────────────────────────────────────────── */}
        <div className="flex flex-col h-screen p-6" style={{ marginTop: "5vh"}}>
         
                <motion.h2
                initial={{ opacity: 0, y: -20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8 }}
                className="text-xl font-bold mb-4"
                >
                Categoría de uso de suelo (área por año)
                <InformationPopUp
            title="Categoría de uso de suelo (área por año)"
            description="Esta gráfica muestra el área cubierta por las distintas categorías de uso de suelo para cada año desde 2000 hasta 2020." 
            bg_color="linear-gradient(to bottom, rgba(50,65,48,0.8), rgba(146,132,59,1) 45%)"
            title_citation="GLC_FCS30D: Uso de Suelo a Nivel Global"
            source="Earth Syst. Sci. Data, 16, 1353–1381 (2024)"
            fullCitation="Zhang, X., Zhao, T., Xu, H., Liu, W., Wang, J., Chen, X., & Liu, L. (2024). GLC_FCS30D: the first global 30 m land-cover dynamics monitoring product with a fine classification system for the period from 1985 to 2022 generated using dense‐time‐series Landsat imagery and the continuous change‐detection method. Earth System Science Data, 16, 1353–1381. https://doi.org/10.5194/essd-16-1353-2024"
            color_citation="#fff"
            /> 
                </motion.h2>
              
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="flex-1 bg-black bg-opacity-30 rounded-md mb-8 p-4"
                  style={{ height: "35%", maxHeight: "35%" }}
                >
                  <MaskedStackedArea />
                </motion.div>

                {/* Chord Diagram */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl font-bold mb-4"
            
          >
            Cambio de categoría de uso de suelo (2000 a 2020) <InformationPopUp
            title="Cambio de categoría de uso de suelo (2000 a 2020) "
            description="En el perímetro de esta gráfica, se muestran las distintas categorías de usos de suelo. Cada flecha dentro del diagrama corresponde a una transición entre un par de estas categorías: la flecha apunta desde el tipo de uso de suelo inicial y hacia el tipo de uso de suelo final, y su tamaño es proporcional al área total que se convirtió." 
            bg_color="linear-gradient(to bottom, rgba(50,65,48,0.8), rgba(146,132,59,1) 45%)"
            title_citation="GLC_FCS30D: Uso de Suelo a Nivel Global"
            source="Earth Syst. Sci. Data, 16, 1353–1381 (2024)"
            fullCitation="Zhang, X., Zhao, T., Xu, H., Liu, W., Wang, J., Chen, X., & Liu, L. (2024). GLC_FCS30D: the first global 30 m land-cover dynamics monitoring product with a fine classification system for the period from 1985 to 2022 generated using dense‐time‐series Landsat imagery and the continuous change‐detection method. Earth System Science Data, 16, 1353–1381. https://doi.org/10.5194/essd-16-1353-2024"
            color_citation="#fff"
            />
          </motion.h2>
          <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex-1 bg-black bg-opacity-30 rounded-md p-4"
                style={{ height: "35%", maxHeight: "35%" }}
            >
              <div >
                <MyChord />
                
          
              </div>
              
            </motion.div>
        </div>

        {/* RIGHT COLUMN: MAP */}
        <div className="flex-none p-6">
          <motion.div
          style={{
              marginTop: "5vh",
              
            }}
            initial={{ opacity: 0, y: -20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="w-full h-[90%] rounded-lg shadow-lg overflow-hidden"
          >
            <motion.h2
                initial={{ opacity: 0, y: -20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8 }}
                className="text-xl font-bold mb-4"
                >
                Histórico del crecimiento urbano
                <InformationPopUp
            title="Histórico del crecimiento urbano"
            description="El mapa muestra la superficie urbanizada a nivel global en los años 2000, 2010 y 2020. Las áreas urbanas en la decada están en morado, mientras que las áreas ya urbanizadas en años anteriores están en gris." 
            title_citation="GLC_FCS30D: Uso de Suelo a Nivel Global"
            source="Earth Syst. Sci. Data, 16, 1353–1381 (2024)"
            bg_color="linear-gradient(to bottom, rgba(50,65,48,0.8), rgba(146,132,59,1) 45%)"
            fullCitation="Zhang, X., Zhao, T., Xu, H., Liu, W., Wang, J., Chen, X., & Liu, L. (2024). GLC_FCS30D: the first global 30 m land-cover dynamics monitoring product with a fine classification system for the period from 1985 to 2022 generated using dense‐time‐series Landsat imagery and the continuous change‐detection method. Earth System Science Data, 16, 1353–1381. https://doi.org/10.5194/essd-16-1353-2024"
            color_citation="#fff"
            /> 
                </motion.h2>
                <motion.h3
                initial={{ opacity: 0, y: -20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8 }}
                className="text-lg font-bold mb-4"
                >
                  Superficie urbanizada (2000, 2005, 2010, 2015, 2020)
                </motion.h3>
                <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="w-full h-full"
            >
            <GenericDeckGLMap/>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}